import { isNavigationFailure, NavigationFailureType } from "vue-router"; // optional safe checks
import { isFalsy } from "@/lib/helpers.js";

import router from "@/router";

// safe helper: parse query into an object from a fullPath or search string
function parseQueryFromSearch(search) {
  const params = Object.fromEntries(new URLSearchParams(search).entries());
  // If you expect repeated keys or special encoding, handle accordingly
  return params;
}

function computeNewQuery(currentQuery, name, value) {
  const next = { ...currentQuery };
  if (!isFalsy(value)) next[name] = value;
  else delete next[name];
  return next;
}

export async function vueUpdateQueryParam(name, value, replace = false) {
  let currentQuery = {};
  const currentPath = window.location.pathname;
  const currentFullPath = window.location.pathname + window.location.search;
  currentQuery = parseQueryFromSearch(window.location.search);
  const nextQuery = computeNewQuery(currentQuery, name, value);

  // compute fullPath for comparison to avoid redundant navigations
  const nextSearch = new URLSearchParams(nextQuery).toString();
  const nextFullPath = nextSearch
    ? `${currentPath}?${nextSearch}`
    : currentPath;

  if (nextFullPath === currentFullPath) return;

  // If router is present, use it so Vue Router stays in sync
  const target = { path: currentPath, query: nextQuery };
  try {
    return router.push(target, replace);
  } catch (err) {
    // ignore benign cancellations/duplicates, rethrow others
    try {
      if (
        isNavigationFailure(err, NavigationFailureType.cancelled) ||
        isNavigationFailure(err, NavigationFailureType.duplicated)
      ) {
        console.error(err);
        return;
      }
    } catch (e) {
      console.log(e);
    }
    throw err;
  }
}

export const getQueryVariables = () => {
  const params = {};
  const search = new URLSearchParams(window.location.search);

  for (const [key, value] of search.entries()) {
    params[key] = value;
  }

  return params;
};

/**
 * Updates the current path with the query param specified with name and value. If the
 * value is passed as a falsey value the query param will be removed.
 *
 * By default this will use pushState to update the URL, passing replace as true will
 * cause the update to use replaceState.
 *
 * @param {String} name Name of the query param
 * @param {(string|null)} value Value of the query param (pass null to remove)
 * @param {Boolean} replace Pass as true to replace the current history entry, default false
 */
export const updateQueryParam = (name, value, replace = false) => {
  const params = getQueryVariables();
  if (!isFalsy(value)) {
    params[name] = value;
  } else {
    delete params[name];
  }

  if (router && typeof router.push === "function") {
    return vueUpdateQueryParam(name, value, replace);
  }

  const newPath = `${window.location.pathname}${encodeQueryParams(params)}`;
  return updatePath(newPath, replace);
};

/**
 * Updates the current path with the new path specified.
 *
 * By default this will use pushState to update the URL, passing replace as true will
 * cause the update to use replaceState.
 *
 * @param {String} newPath new path to set (relative)
 * @param {Boolean} replace Pass as true to replace the current history entry, default false
 */
export const updatePath = (newPath, replace = false) => {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const currentState = history.state || {};
  if (newPath === currentPath) return;

  if (replace) {
    return history.replaceState(currentState, "", newPath);
  } else {
    return history.pushState(currentState, "", newPath);
  }
};

export const encodeQueryParams = (params) => {
  const encoded = Object.keys(params)
    .map((key) => key + "=" + window.encodeURIComponent(params[key]))
    .join("&");
  return encoded.length ? `?${encoded}` : "";
};

export const getPathValueAt = (index = 0) => {
  return window.location.pathname.split("/").filter((item) => item.length > 0)[
    index
  ];
};

export const updateTabParam = async (tabsId, tabName, modifyHistory = true) => {
  const params = getQueryVariables();
  const encodedTab = `${tabsId}_${tabName}`;
  const activeTabs = params.tab ? params.tab.split(",") : [];
  // Remove self & siblings
  let param = activeTabs.filter((tab) => tab.indexOf(`${tabsId}_`) < 0);
  // Handle adding or removing tab
  if (tabName) {
    param = param.concat([encodedTab]);
  }
  if (modifyHistory) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        await updateQueryParam("tab", param.join());
        resolve();
      });
    });
  } else {
    return param.join();
  }
};

export const comparePaths = (path1, path2) => {
  const normalizePath = (url) => url.match(/^[^?#]+/)[0];
  return normalizePath(path1) === normalizePath(path2);
};
